import React, { useState } from 'react';
import { UniversityQuery } from '../../types';

interface FilterSidebarProps {
  filters: UniversityQuery;
  onFiltersChange: (filters: UniversityQuery) => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ filters, onFiltersChange }) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const updateFilters = (newFilters: Partial<UniversityQuery>) => {
    onFiltersChange({ ...filters, ...newFilters, page: 1 }); // Reset to page 1 when filters change
  };

  const handleCheckboxChange = (field: keyof UniversityQuery, checked: boolean, value?: any) => {
    if (field === 'environment') {
      const currentEnvironment = filters.environment || [];
      let newEnvironment: string[];
      
      if (checked) {
        // Add the value to the environment array
        newEnvironment = [...currentEnvironment, value];
      } else {
        // Remove the value from the environment array
        newEnvironment = currentEnvironment.filter(env => env !== value);
      }
      
      updateFilters({ environment: newEnvironment.length > 0 ? newEnvironment : undefined });
    } else if (field === 'application_tags') {
      const currentTags = filters.application_tags || [];
      let newTags: string[];
      
      if (checked) {
        newTags = [...currentTags, value];
      } else {
        newTags = currentTags.filter(tag => tag !== value);
      }
      
      updateFilters({ application_tags: newTags.length > 0 ? newTags : undefined });
    } else {
      // For other checkbox fields, just log for now
      console.log(`Checkbox ${field} ${checked ? 'checked' : 'unchecked'}`, value);
    }
  };

  return (
    <div className="w-[300px] flex flex-col space-y-4">
      {/* Search and Sort */}
      <div className="bg-white dark:bg-gray-800 border border-[#e5e7eb] dark:border-gray-700 rounded-xl p-5 space-y-2">
        <div className="relative">
          <input 
            className="w-full rounded-md border border-[#e5e7eb] dark:border-gray-600 bg-[#f5f6fa] dark:bg-gray-700 py-2 pl-10 pr-4 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" 
            placeholder="Search college name" 
            type="text"
            value={filters.search || ''}
            onChange={(e) => updateFilters({ search: e.target.value })}
          />
          <i className="fas fa-search absolute left-3 top-2.5 text-gray-400 dark:text-gray-500"></i>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Sort by</label>
          <select 
            className="w-full border border-[#e5e7eb] dark:border-gray-600 rounded-md py-2 px-3 text-sm bg-[#f5f6fa] dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            value={`${filters.sort_by || 'name'}-${filters.order || 'asc'}`}
            onChange={(e) => {
              const [sort_by, order] = e.target.value.split('-') as [any, 'asc' | 'desc'];
              updateFilters({ sort_by, order });
            }}
          >
            <option value="name-asc">A-Z (Default)</option>
            <option value="name-desc">Z-A</option>
            <option value="acceptance_rate-asc">Most Selective First</option>
            <option value="acceptance_rate-desc">Least Selective First</option>
            <option value="net_price_per_year-asc">Lowest Cost</option>
            <option value="net_price_per_year-desc">Highest Cost</option>
            <option value="undergrad_students-asc">Smallest First</option>
            <option value="undergrad_students-desc">Largest First</option>
          </select>
        </div>
      </div>

      {/* Lists and Filters */}
      <div className="bg-white dark:bg-gray-800 border border-[#e5e7eb] dark:border-gray-700 rounded-xl p-5 space-y-4">
        {/* Lists */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Lists</label>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="my-list" 
                type="checkbox"
                checked={!!filters.mylist}
                onChange={(e) => updateFilters({ mylist: e.target.checked || undefined })}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="my-list">
                My College List
              </label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="suggested" 
                type="checkbox"
                onChange={(e) => handleCheckboxChange('suggested' as any, e.target.checked)}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="suggested">
                Suggested For You
              </label>
            </div>
          </div>
        </div>

        {/* Application Tags */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Application tag</label>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300" 
                id="reach" 
                type="checkbox"
                checked={filters.application_tags?.includes('reach') || false}
                onChange={(e) => handleCheckboxChange('application_tags' as any, e.target.checked, 'reach')}
              />
              <label className="text-sm text-gray-700 ml-2" htmlFor="reach">
                <span className="inline-block bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                  Reach
                </span>
              </label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300" 
                id="target" 
                type="checkbox"
                checked={filters.application_tags?.includes('target') || false}
                onChange={(e) => handleCheckboxChange('application_tags' as any, e.target.checked, 'target')}
              />
              <label className="text-sm text-gray-700 ml-2" htmlFor="target">
                <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  Target
                </span>
              </label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300" 
                id="safety" 
                type="checkbox"
                checked={filters.application_tags?.includes('safety') || false}
                onChange={(e) => handleCheckboxChange('application_tags' as any, e.target.checked, 'safety')}
              />
              <label className="text-sm text-gray-700 ml-2" htmlFor="safety">
                <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                  Safety
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Student Body Size */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Student body size
            <i className="fas fa-info-circle text-gray-400 dark:text-gray-500 ml-1 text-xs"></i>
          </label>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="small" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_undergrads: 0, max_undergrads: 2000 });
                  } else {
                    updateFilters({ min_undergrads: undefined, max_undergrads: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="small">Small &lt;2k</label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="medium" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_undergrads: 2000, max_undergrads: 5000 });
                  } else {
                    updateFilters({ min_undergrads: undefined, max_undergrads: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="medium">Medium 2-5k</label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="large" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_undergrads: 5000, max_undergrads: 15000 });
                  } else {
                    updateFilters({ min_undergrads: undefined, max_undergrads: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="large">Large 5-15k</label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="xlarge" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_undergrads: 15000 });
                  } else {
                    updateFilters({ min_undergrads: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="xlarge">Extra Large &gt;15k</label>
            </div>
          </div>
        </div>

        {/* Selectivity */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Selectivity
            <i className="fas fa-info-circle text-gray-400 dark:text-gray-500 ml-1 text-xs"></i>
          </label>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="most-selective" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_acceptance_rate: 0, max_acceptance_rate: 15 });
                  } else {
                    updateFilters({ min_acceptance_rate: undefined, max_acceptance_rate: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="most-selective">Most selective &lt;15%</label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="very-selective" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_acceptance_rate: 15, max_acceptance_rate: 30 });
                  } else {
                    updateFilters({ min_acceptance_rate: undefined, max_acceptance_rate: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="very-selective">Very selective 15-30%</label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="selective" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_acceptance_rate: 30, max_acceptance_rate: 60 });
                  } else {
                    updateFilters({ min_acceptance_rate: undefined, max_acceptance_rate: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="selective">Selective 30-60%</label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="less-selective" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_acceptance_rate: 60, max_acceptance_rate: 90 });
                  } else {
                    updateFilters({ min_acceptance_rate: undefined, max_acceptance_rate: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="less-selective">Less selective 60-90%</label>
            </div>
            <div className="flex items-center">
              <input 
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" 
                id="not-selective" 
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFilters({ min_acceptance_rate: 90, max_acceptance_rate: 100 });
                  } else {
                    updateFilters({ min_acceptance_rate: undefined, max_acceptance_rate: undefined });
                  }
                }}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 ml-2" htmlFor="not-selective">Not very selective &gt;90%</label>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white dark:bg-gray-800 border border-[#e5e7eb] dark:border-gray-700 rounded-xl p-5">
        <button 
          className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 bg-[#f5f6fa] dark:bg-gray-700 hover:bg-[#eef0f6] dark:hover:bg-gray-600 border border-[#e5e7eb] dark:border-gray-600 rounded-md px-3 py-2"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
        >
          <span>Advanced filters</span>
          <i className={`fas ${showAdvancedFilters ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-500 dark:text-gray-400`}></i>
        </button>
        
        {showAdvancedFilters && (
          <div className="mt-4 space-y-4">
            {/* Cost */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Average cost per year
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400 text-sm">Less than</span>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1.5 text-gray-400 dark:text-gray-500 text-sm">$</span>
                  <input 
                    type="number" 
                    min="0" 
                    step="1000" 
                    placeholder="e.g. 30000" 
                    className="w-full rounded-md border border-[#e5e7eb] dark:border-gray-600 bg-[#f5f6fa] dark:bg-gray-700 py-2 pl-7 pr-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.max_net_price || ''}
                    onChange={(e) => updateFilters({ max_net_price: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
                <span className="text-gray-500 dark:text-gray-400 text-sm">dollars</span>
              </div>
            </div>

            {/* SAT Range */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Average SAT range
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input 
                  type="number" 
                  min="400" 
                  max="1600" 
                  placeholder="Min" 
                  className="rounded-md border border-[#e5e7eb] dark:border-gray-600 bg-[#f5f6fa] dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.sat_min || ''}
                  onChange={(e) => updateFilters({ sat_min: e.target.value ? Number(e.target.value) : undefined })}
                />
                <input 
                  type="number" 
                  min="400" 
                  max="1600" 
                  placeholder="Max" 
                  className="rounded-md border border-[#e5e7eb] dark:border-gray-600 bg-[#f5f6fa] dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.sat_max || ''}
                  onChange={(e) => updateFilters({ sat_max: e.target.value ? Number(e.target.value) : undefined })}
                />
              </div>
            </div>

            {/* ACT Range */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Average ACT range
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input 
                  type="number" 
                  min="1" 
                  max="36" 
                  placeholder="Min" 
                  className="rounded-md border border-[#e5e7eb] dark:border-gray-600 bg-[#f5f6fa] dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.act_min || ''}
                  onChange={(e) => updateFilters({ act_min: e.target.value ? Number(e.target.value) : undefined })}
                />
                <input 
                  type="number" 
                  min="1" 
                  max="36" 
                  placeholder="Max" 
                  className="rounded-md border border-[#e5e7eb] dark:border-gray-600 bg-[#f5f6fa] dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.act_max || ''}
                  onChange={(e) => updateFilters({ act_max: e.target.value ? Number(e.target.value) : undefined })}
                />
              </div>
            </div>

            {/* Additional Factors */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Additional factors
              </label>
              <div className="flex flex-col space-y-2">
                {['Big City', 'Large City', 'Midsize City', 'Small City', 'College Town', 'Suburb', 'Beach'].map((factor) => (
                  <label key={factor} className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <input 
                      type="checkbox" 
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                      checked={filters.environment?.includes(factor) || false}
                      onChange={(e) => handleCheckboxChange('environment' as any, e.target.checked, factor)}
                    />
                    <span className="ml-2">{factor}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSidebar;
